import { Component } from '@theme/component'
import { ThemeEvents } from '@theme/events'
import { requestIdleCallback as requestIdle, useReduced } from '@theme/maths'
import { mediaQueryMobile } from '@theme/use-device-detection'
import { useIntersection } from '@theme/use-intersection'

const DeferredMediaConfig = {
  maxContentWaitAttempts: 20,
  viewportRootMargin: '200px 0px 200px 0px'
}

export class DeferredMedia extends Component {
  #mediaAbortController = null
  #intersectionCleanup = null
  #pausedByViewport = false

  isPlaying = false

  /** @returns {string[]} */
  static get observedAttributes() {
    return ['playing']
  }

  /** @param {Event} event */
  onPosterClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    this.toggleMedia()
  }

  pauseOnDialogClose = () => {
    this.pauseMedia()
  }

  /** @param {CustomEvent} event */
  handleMediaStartedPlaying = (event) => {
    if (event.detail?.resource === this) {
      return
    }

    this.pauseMedia()
  }

  /** @param {MouseEvent} event */
  #onTapToPlay = (event) => {
    if (this.isInitialized) {
      return
    }

    if (event.target instanceof Element && event.target.closest('a')) {
      return
    }

    this.paused = false
    this._waitForContentAndInit()
  }

  /** @returns {HTMLElement | null} */
  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]')
  }

  /** @returns {HTMLElement | null} */
  get controlledElement() {
    const controlledId = this.getAttribute('aria-controls')
    return controlledId ? document.getElementById(controlledId) : null
  }

  /** @returns {boolean} */
  get autoplay() {
    return this.hasAttribute('autoplay')
  }

  /** @returns {boolean} */
  get playing() {
    return this.hasAttribute('playing')
  }

  /** @returns {boolean} */
  get loaded() {
    return this.hasAttribute('loaded') || this.hasAttribute('data-media-loaded')
  }

  /** @returns {Record<string, () => Promise<void>>} */
  get player() {
    if (this.playerProxy) {
      return this.playerProxy
    }
    this.playerProxy = new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          const resolvedTarget = await target
          this.playerHandler(resolvedTarget, prop)
        }
      }
    })
    return this.playerProxy
  }

  /** @param {Event} [event] */
  showDeferredMedia(event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.playMedia()
  }

  /**
   * @param {boolean} [focus]
   * @returns {HTMLElement | null}
   */
  loadContent(focus = true) {
    const player = this.playerTarget()
    this.markMediaLoaded()

    if (focus && player instanceof HTMLElement) {
      player.focus()
    }

    return player
  }

  /** @param {Event} [event] */
  toggleMedia(event) {
    event?.preventDefault()
    event?.stopPropagation()

    if (this.playing) {
      this.pauseMedia()
      return
    }

    this.playMedia()
  }

  playMedia() {
    this.paused = false
    this.play()
  }

  pauseMedia() {
    this.paused = true
    this.pause()
    this.isPlaying = false
    this.updatePlayPauseHint(false)
  }

  /** @param {boolean} isPlaying */
  updatePlayPauseHint(isPlaying) {
    const playIcon = this.querySelector('.video-icon-play')
    const pauseIcon = this.querySelector('.video-icon-pause')
    if (!(playIcon && pauseIcon)) {
      return
    }

    playIcon.classList.toggle('hidden', isPlaying)
    pauseIcon.classList.toggle('hidden', !isPlaying)
  }

  markMediaLoaded() {
    this.setAttribute('loaded', '')
    this.setAttribute('data-media-loaded', 'true')
    this.closest('.media')?.classList.remove('loading')
  }

  connectedCallback() {
    super.connectedCallback()
    this.#mediaAbortController = new AbortController()
    this.#bindMediaListeners(this.#mediaAbortController.signal)

    if (this.playing) {
      return
    }

    if (this.getAttribute('loading') === 'eager') {
      this._waitForContentAndInit()
      return
    }

    if (this.autoplay) {
      this._setupAutoplayVisibility()
    }
  }

  disconnectedCallback() {
    this.#mediaAbortController?.abort()
    this.#intersectionCleanup?.()
    super.disconnectedCallback()
  }

  /** @param {AbortSignal} signal */
  #bindMediaListeners(signal) {
    this.posterElement?.addEventListener('click', this.onPosterClick, {
      signal
    })

    if (this.autoplay) {
      return
    }

    document.addEventListener(
      ThemeEvents.mediaStartedPlaying,
      this.handleMediaStartedPlaying,
      { signal }
    )
    document.addEventListener('dialog:close', this.pauseOnDialogClose, {
      signal
    })
  }

  #pauseForViewport() {
    if (!this.playing) {
      return
    }

    this.#pausedByViewport = true
    this.pause()
  }

  /** @returns {(() => void) | undefined} */
  init() {
    if (this.isInitialized) {
      return undefined
    }
    this.isInitialized = true

    if (!this.paused) {
      this.play()
    }

    return () => {
      this.pause()
    }
  }

  play() {
    if (!this.playing) {
      this.player.play()
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause()
    }
  }

  /**
   * @param {string} name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'playing') {
      return
    }

    if (oldValue === null && newValue === '') {
      this.isPlaying = true
      this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }))
      return
    }

    if (newValue === null) {
      this.isPlaying = false
      this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }))
    }
  }

  /** @returns {boolean} */
  get _autoplaySuppressed() {
    return mediaQueryMobile.matches || useReduced()
  }

  _setupAutoplayVisibility() {
    if (this._autoplaySuppressed) {
      this._enableTapToPlay()
      return
    }

    const observer = useIntersection({
      rootMargin: DeferredMediaConfig.viewportRootMargin
    })
    this.#intersectionCleanup = observer.observe(this, (entry) => {
      this._onViewportChange(entry.isIntersecting)
    })
  }

  /** @param {boolean} isIntersecting */
  _onViewportChange(isIntersecting) {
    if (!isIntersecting) {
      this.#pauseForViewport()
      return
    }

    if (this.paused) {
      return
    }

    if (!this.isInitialized) {
      this._waitForContentAndInit()
      return
    }

    if (!this.#pausedByViewport) {
      return
    }

    this.#pausedByViewport = false
    this.play()
  }

  _enableTapToPlay() {
    this.addEventListener('click', this.#onTapToPlay, {
      signal: this.#mediaAbortController?.signal
    })
  }

  /** @param {number} [attempts] */
  _waitForContentAndInit(attempts = 0) {
    if (this.querySelector('template') || this.querySelector('video')) {
      this.init()
      return
    }

    if (attempts > DeferredMediaConfig.maxContentWaitAttempts) {
      return
    }

    requestIdle(() => this._waitForContentAndInit(attempts + 1))
  }
}
